import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { views } from "@onsip/web/app/phone/views";
import { MatStepper } from "@angular/material/stepper";
import { map, take } from "rxjs/operators";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import { Observable, Subscription } from "rxjs";
import { UserAddress } from "@onsip/common/services/api/resources/userAddress/user-address.service";
import {
  GroupAddress,
  GroupAddressService
} from "@onsip/common/services/api/resources/groupAddress/group-address.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { DynamicPricingService } from "@onsip/common/services/dynamic-pricing.service";
import { AddressSummary } from "@onsip/common/services/api/resources/addressSummary/address-summary";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { FormatSingularAddressTypePipe } from "@onsip/web/features/shared/pipes/format-singular-address-type.pipe";
import { STEPPER_GLOBAL_OPTIONS } from "@angular/cdk/stepper";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import { BreakpointObserver, Breakpoints } from "@angular/cdk/layout";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { MemberType } from "@onsip/web/features/administrators/groups/selectMemberDragAndDrop/select-member-drag-drop.component";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

const PARALLEL = "parallel";

export enum SelectMemberType {
  user = "Users",
  external = "External SIP",
  telephone_number = "External phone number",
  all = "View all"
}

@Component({
  selector: "onsip-group-setup",
  templateUrl: "./group-setup.component.html",
  styleUrls: ["./group-setup.component.scss"],
  providers: [
    FormatSingularAddressTypePipe,
    {
      provide: STEPPER_GLOBAL_OPTIONS,
      useValue: { displayDefaultIndicatorType: false }
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class GroupSetupComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-admin-host-grid")
  @HostBinding("class.onsip-grid-content")
  _dontUse = true;
  @ViewChild("stepper") stepper!: MatStepper;
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  E2eLocators = E2eLocators;

  groupInfo = new FormGroup({
    basicStep: new FormGroup({
      Name: new FormControl("", [
        Validators.required,
        this.validateExistName(),
        Validators.pattern(NAME_VALIDATOR_REGEX)
      ]),
      sipAddress: new FormControl("", Validators.required),
      DefaultAddress: new FormControl(""),
      Ordering: new FormControl(PARALLEL),
      Timeout: new FormControl(""),
      extensions: new FormControl([])
    }),
    memberStep: new FormGroup({
      members: new FormControl([], [Validators.required, Validators.maxLength(10)])
    })
  });

  memberType = new FormControl(MemberType.all, { nonNullable: true });

  searchMember = new FormControl("", { nonNullable: true });
  durationArr: Array<number> = [];
  existedGroups: Array<GroupAddress> = [];
  assignMembersFormControl = new FormControl([], Validators.required);
  views = views;
  pricingText = "";
  allExtensions: Array<string> = [];
  extHint = "";
  domain = "";
  selectedMembers: Array<UserAddress> = [];
  addressSummaries: Array<AddressSummary> = [];
  phonesList: Array<string> = [];
  private unsubscriber = new Subscription();
  mobileView!: Observable<boolean>;
  maxItemCount = 10;
  columnCount = 4;
  memberTypes = Object.values(MemberType);

  constructor(
    private groups: GroupAddressService,
    private snackbar: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private router: Router,
    private cdRef: ChangeDetectorRef,
    private dynamicPricing: DynamicPricingService,
    private addressSummaryService: AddressSummaryService,
    private singularAddressPipe: FormatSingularAddressTypePipe,
    private breakpointObserver: BreakpointObserver,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    this.addressSummaryService.addressSummaryBrowse();

    this.setPricing();
    // Ring duration data preparation
    for (let i = 0; i < 24; i++) {
      this.durationArr.push((i + 1) * 5);
    }

    this.timeout.patchValue(this.durationArr[0]);

    this.unsubscriber.add(
      this.name.valueChanges.subscribe(data => {
        this.sipAddress.setValue(data);
      })
    );

    this.unsubscriber.add(
      this.userService.orgState.pipe(take(1)).subscribe(users => (this.domain = users[0].domain))
    );

    this.mobileView = this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(map(res => res.matches));

    this.unsubscriber.add(this.groups.orgState.subscribe(groups => (this.existedGroups = groups)));

    this.unsubscriber.add(
      this.addressSummaryService.orgState.subscribe(summaries => {
        this.addressSummaries = summaries;
      })
    );
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  get name(): FormControl {
    return this.groupInfo.controls.basicStep.get("Name") as FormControl;
  }

  get sipAddress(): FormControl {
    return this.groupInfo.controls.basicStep.get("sipAddress") as FormControl;
  }

  get defaultAddress(): FormControl {
    return this.groupInfo.controls.basicStep.get("DefaultAddress") as FormControl;
  }

  get timeout(): FormControl {
    return this.groupInfo.controls.basicStep.get("Timeout") as FormControl;
  }

  get extensions(): FormControl {
    return this.groupInfo.controls.basicStep.get("extensions") as FormControl;
  }

  get ordering(): FormControl {
    return this.groupInfo.controls.basicStep.get("Ordering") as FormControl;
  }

  get members(): FormControl {
    return this.groupInfo.controls.memberStep.get("members") as FormControl;
  }

  validateExistName(): ValidatorFn {
    // @ts-ignore
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        // eslint-disable-next-line no-null/no-null
        return null;
      }

      if (this.existedGroups.length) {
        const groupWithSameName = this.existedGroups.find(g => g.username === value);
        if (groupWithSameName) {
          return { notUnique: true };
        }
      }
    };
  }

  private showNoMembersErrorSnackbar(): void {
    this.snackbar.openSnackBar("Please select more than 0 users.", "error");
  }

  private setPricing(): void {
    this.dynamicPricing.getDynamicPricing("GroupAddress", false).then(result => {
      this.pricingText = result;
      this.cdRef.markForCheck();
    });
  }

  goToAddUsers(): void {
    this.groupInfo.controls.basicStep.markAllAsTouched();
    if (this.groupInfo.controls.basicStep.invalid) {
      return;
    }
    this.stepper.next();
  }

  goToReview(): void {
    if (this.members.invalid) {
      this.showNoMembersErrorSnackbar();
      return;
    }
    this.stepper.next();
  }

  backStep(): void {
    this.stepper.previous();
  }

  saveForm() {
    if (this.groupInfo.invalid) {
      return;
    }
    const SelectedAddresses = (this.members.value as Array<AddressSummary>)
      .map(member => `${member.username}@${member.domain}`)
      .filter((member): member is string => !!member);

    const params = {
      Name: this.name.value,
      Username: this.sipAddress.value,
      DefaultAddress: this.defaultAddress.value,
      Ordering: this.ordering.value,
      Timeout: this.timeout.value,
      SelectedAddresses
    };

    this.groups.groupAddressAdd(params)?.then(res => {
      if (res.status === "success") {
        this.extensionsListRef.editExtensions(params.Username);
        this.snackbar.openSnackBar(
          this.translate.instant("ONSIP_I18N.VALUE_HAS_BEEN_ADDED", {
            value: params.Username
          }),
          "success"
        );
        this.router.navigate([views.GROUPS]);
      } else {
        this.snackbar.openSnackBar(res.data.message, "error");
        this.groups.clearErrors();
      }
    });
  }

  getTextForViewFromAddress(aor: string): string {
    const address = this.addressSummaries.find(address => address.aor === aor);
    if (address) {
      return `(${this.singularAddressPipe.transform(address.addressType)}) ${address.name}`;
    }
    return "None";
  }

  handleCancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: "Cancel setup?",
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            void this.router.navigate([views.GROUPS]);
          }
        })
    );
  }

  getErrorMessage(errors: ValidationErrors | null): string {
    if (errors?.invalidExtension || errors?.minLength) {
      return this.translate.instant(
        "ONSIP_I18N.EXTENSIONS_MUST_BE_BETWEEN_26_DIGITS_AND_BEGIN_WITH_EITHER__OR_A_NONZERO_DIGIT"
      );
    } else if (errors?.repeatExtension) {
      return this.translate.instant("ONSIP_I18N.EXTENSION_ALREADY_IN_USE");
    } else return "";
  }

  editBasicSettings(): void {
    this.stepper.selectedIndex = 0;
  }

  editMembers() {
    this.stepper.previous();
  }

  selectMemberType(type: MemberType): SelectMemberType {
    return SelectMemberType[type];
  }

  getNameError() {
    const control = this.groupInfo.controls.basicStep.controls.Name;
    if (control.hasError("required")) {
      return this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED");
    } else if (control.hasError("notUnique")) {
      return "This name already used.";
    } else if (control.hasError("pattern")) {
      return this.translate.instant(
        "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
      );
    }
    return "";
  }
}
